export default function Pricing() {
  const plans = [
    {
      name: 'Starter',
      price: '$250',
      description: 'Perfect for small businesses just getting started online',
      features: [
        'Up to 5 pages',
        'Mobile responsive design',
        'Contact form',
        'Social media links',
        'Basic SEO setup',
        '1 month support'
      ],
      popular: false,
      gradient: 'from-gray-600 to-gray-700'
    },
    {
      name: 'Professional',
      price: '$400',
      description: 'Most popular choice for growing businesses',
      features: [
        'Up to 7 pages',
        'Custom design',
        'Advanced contact forms',
        'Google Maps integration',
        'Advanced SEO',
        'Blog setup',
        '3 months support',
        'Analytics setup'
      ],
      popular: true,
      gradient: 'from-cyan-500 to-blue-600'
    },
    {
      name: 'E-commerce',
      price: '$1000',
      description: 'Complete online store solution',
      features: [
        'Unlimited pages',
        'Product catalog',
        'Shopping cart',
        'Payment gateway',
        'Inventory management',
        'Order tracking',
        'Customer accounts',
        '6 months support',
        'Marketing tools'
      ],
      popular: false,
      gradient: 'from-purple-600 to-pink-600'
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="pricing" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
            <i className="ri-price-tag-3-line"></i>
            Transparent Pricing
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
            Simple,{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Affordable Pricing
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Choose the perfect plan for your business. No hidden fees, no surprises.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${
                plan.popular ? 'ring-2 ring-cyan-500 transform lg:scale-105' : ''
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                  <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs sm:text-sm font-bold rounded-full shadow-lg whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {plan.name}
                </h3>
                <div className="mb-2 sm:mb-3">
                  <span className={`text-4xl sm:text-5xl font-bold bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}>
                    {plan.price}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed px-2">
                  {plan.description}
                </p>
              </div>

              {/* Features List */}
              <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br ${plan.gradient} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={scrollToContact}
                className={`w-full px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  plan.popular
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-cyan-500/50'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10">
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 text-center">
            <div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white shadow-md flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-cyan-600"></i>
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">Money-Back Guarantee</h4>
              <p className="text-xs sm:text-sm text-gray-600">Not satisfied? Get a full refund within 30 days</p>
            </div>
            <div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white shadow-md flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-calendar-line text-2xl sm:text-3xl text-cyan-600"></i>
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">Fast Delivery</h4>
              <p className="text-xs sm:text-sm text-gray-600">Most projects completed within 7-14 days</p>
            </div>
            <div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white shadow-md flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-customer-service-2-line text-2xl sm:text-3xl text-cyan-600"></i>
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">Ongoing Support</h4>
              <p className="text-xs sm:text-sm text-gray-600">Free support included with every plan</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
